import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { invoiceLog } from "./invoiceLogger";
import { Play, CheckCircle2, AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";

// ─── Test invoice samples ─────────────────────────────────────────────────────
const TEST_CASES = [
  {
    id: "straight",
    name: "Factura dreta",
    description: "Factura estàndard, bona il·luminació, text net",
    tags: ["standard", "high-quality"],
    expectedFields: ["supplier_name", "invoice_number", "invoice_date", "total"],
    minConfidence: 85,
    mockOCRData: {
      supplier_name: "Carns Puig S.L.",
      supplier_nif: "B12345678",
      invoice_number: "2024-001234",
      invoice_date: "2024-01-15",
      subtotal: 450.00,
      vat_amount: 94.50,
      total: 544.50,
      ocr_confidence: 95,
      tax_lines: [{ base: 450.00, vat_pct: 21, vat_amount: 94.50, surcharge_pct: 0, surcharge_amount: 0 }],
      items: [
        { description: "Pit de pollastre", quantity: 10, unit: "kg", unit_price: 5.50, line_total: 55.00, vat_pct: 10 },
        { description: "Costella de porc", quantity: 25, unit: "kg", unit_price: 7.80, line_total: 195.00, vat_pct: 10 },
        { description: "Vedella filet",    quantity: 5,  unit: "kg", unit_price: 40.00, line_total: 200.00, vat_pct: 21 },
      ],
    },
  },
  {
    id: "tilted",
    name: "Factura inclinada",
    description: "Foto feta de biaix, requereix correcció de perspectiva",
    tags: ["perspective", "medium-quality"],
    expectedFields: ["supplier_name", "total"],
    minConfidence: 70,
    mockOCRData: {
      supplier_name: "Distribucions Martí",
      supplier_nif: "A87654321",
      invoice_number: "FAC-2024-0567",
      invoice_date: "2024-02-03",
      subtotal: 230.00,
      vat_amount: 48.30,
      total: 278.30,
      ocr_confidence: 72,
      tax_lines: [{ base: 230.00, vat_pct: 21, vat_amount: 48.30 }],
      items: [
        { description: "Oli d'oliva verge extra 5L", quantity: 6, unit: "u", unit_price: 22.50, line_total: 135.00, vat_pct: 10 },
        { description: "Vinagre balsàmic",           quantity: 4, unit: "u", unit_price: 8.50,  line_total: 34.00,  vat_pct: 10 },
        { description: "Mostassa Dijón 1kg",          quantity: 3, unit: "u", unit_price: 5.20,  line_total: 15.60,  vat_pct: 10 },
      ],
    },
  },
  {
    id: "wrinkled",
    name: "Factura arrugada",
    description: "Paper doblegat, zones poc llegibles",
    tags: ["physical-damage", "low-quality"],
    expectedFields: ["supplier_name", "total"],
    minConfidence: 55,
    mockOCRData: {
      supplier_name: "Lactis del Pirineu",
      supplier_nif: null,
      invoice_number: "F-890",
      invoice_date: "2024-01-28",
      subtotal: 187.50,
      vat_amount: 12.50,
      total: 200.00,
      ocr_confidence: 58,
      notes: "Zona central poc llegible. Número de factura pot ser F-899. Verificar totals.",
      tax_lines: [{ base: 150.00, vat_pct: 4, vat_amount: 6.00 }, { base: 37.50, vat_pct: 10, vat_amount: 3.75 }],
      items: [
        { description: "Llet sencera 1L (caixa 12u)", quantity: 5, unit: "caix", unit_price: 12.00, line_total: 60.00, vat_pct: 4 },
        { description: "Formatge semi 500g",           quantity: 8, unit: "u",    unit_price: 5.50,  line_total: 44.00, vat_pct: 4 },
        { description: "Iogurt natural 4 pack",        quantity: 6, unit: "pack", unit_price: 3.75,  line_total: 22.50, vat_pct: 10 },
      ],
    },
  },
  {
    id: "shadowed",
    name: "Factura amb ombres",
    description: "Foto amb ombres fortes o brillos, requereix normalització",
    tags: ["lighting", "shadow-removal"],
    expectedFields: ["supplier_name", "invoice_number"],
    minConfidence: 60,
    mockOCRData: {
      supplier_name: "Verdures i Hortalisses Can Pau",
      supplier_nif: "B34567890",
      invoice_number: "ALB-20240156",
      invoice_date: "2024-02-10",
      subtotal: 320.00,
      vat_amount: 32.00,
      total: 352.00,
      ocr_confidence: 63,
      notes: "Ombra a la cantonada superior dreta pot haver afectat lectura del NIF.",
      tax_lines: [{ base: 320.00, vat_pct: 10, vat_amount: 32.00 }],
      items: [
        { description: "Tomaquet rama kg", quantity: 30, unit: "kg", unit_price: 2.20, line_total: 66.00, vat_pct: 10 },
        { description: "Enciam fulla de roure", quantity: 20, unit: "u", unit_price: 1.50, line_total: 30.00, vat_pct: 10 },
        { description: "Ceba blanca kg",  quantity: 25, unit: "kg", unit_price: 0.90, line_total: 22.50, vat_pct: 10 },
      ],
    },
  },
  {
    id: "multi_vat",
    name: "Múltiples tipus IVA",
    description: "Factura amb IVA al 4%, 10% i 21% simultàniament",
    tags: ["complex-vat", "standard"],
    expectedFields: ["supplier_name", "tax_lines", "total"],
    minConfidence: 80,
    mockOCRData: {
      supplier_name: "Distribucions Alimentàries Bou S.A.",
      supplier_nif: "A56789012",
      invoice_number: "2024/0234",
      invoice_date: "2024-02-15",
      subtotal: 1250.00,
      vat_amount: 123.00,
      total: 1373.00,
      ocr_confidence: 88,
      tax_lines: [
        { base: 400.00, vat_pct: 4,  vat_amount: 16.00, surcharge_pct: 0.5,  surcharge_amount: 2.00 },
        { base: 650.00, vat_pct: 10, vat_amount: 65.00, surcharge_pct: 1.4,  surcharge_amount: 9.10 },
        { base: 200.00, vat_pct: 21, vat_amount: 42.00, surcharge_pct: 5.2,  surcharge_amount: 10.40 },
      ],
      items: [
        { description: "Pa integral 500g", quantity: 48, unit: "u",  unit_price: 1.80, line_total: 86.40, vat_pct: 4 },
        { description: "Llet desnatada", quantity: 24, unit: "u", unit_price: 0.85, line_total: 20.40, vat_pct: 4 },
        { description: "Pollastre sencer kg", quantity: 30, unit: "kg", unit_price: 4.50, line_total: 135.00, vat_pct: 10 },
        { description: "Salmó fumat 200g", quantity: 10, unit: "u", unit_price: 8.90, line_total: 89.00, vat_pct: 10 },
        { description: "Vi negre criança 75cl", quantity: 24, unit: "u", unit_price: 6.50, line_total: 156.00, vat_pct: 21 },
        { description: "Cava brut reserva", quantity: 6, unit: "u", unit_price: 9.80, line_total: 58.80, vat_pct: 21 },
      ],
    },
  },
  {
    id: "poor_quality",
    name: "Factura baixa qualitat",
    description: "Text borrós, fotocòpia de fotocòpia, OCR molt limitat",
    tags: ["very-low-quality", "manual-review"],
    expectedFields: ["total"],
    minConfidence: 35,
    mockOCRData: {
      supplier_name: null,
      supplier_nif: null,
      invoice_number: null,
      invoice_date: null,
      subtotal: 0,
      vat_amount: 0,
      total: 89.50,
      ocr_confidence: 28,
      notes: "ATENCIÓ: Qualitat molt baixa. Camps no llegibles. Revisió manual necessària.",
      tax_lines: [],
      items: [],
    },
  },
];

function TestResult({ result }) {
  const [open, setOpen] = useState(false);
  const statusColor = result.status === "pass" ? "text-emerald-700 bg-emerald-100"
    : result.status === "warn" ? "text-amber-700 bg-amber-100"
    : "text-red-700 bg-red-100";

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors text-left"
      >
        <div className="flex items-center gap-3 min-w-0">
          {result.status === "pass"
            ? <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
            : <AlertTriangle className={`w-4 h-4 flex-shrink-0 ${result.status === "warn" ? "text-amber-500" : "text-red-500"}`} />}
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-800 truncate">{result.name}</p>
            <p className="text-xs text-gray-400 truncate">{result.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Badge className={`border-0 text-xs ${statusColor}`}>{result.status}</Badge>
          <Badge className="border-0 text-xs bg-gray-100 text-gray-600">OCR {result.confidence}%</Badge>
          {open ? <ChevronUp className="w-3.5 h-3.5 text-gray-400" /> : <ChevronDown className="w-3.5 h-3.5 text-gray-400" />}
        </div>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3 border-t border-gray-100 pt-3">
          <div className="flex flex-wrap gap-1.5">
            {result.tags?.map(t => (
              <span key={t} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-[10px] font-semibold rounded-full">{t}</span>
            ))}
          </div>
          <div className="space-y-1.5">
            {result.checks.map((c, i) => (
              <div key={`cat-${i}`} className={`flex items-center gap-2 text-xs ${c.pass ? "text-emerald-700" : "text-red-600"}`}>
                {c.pass ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
                {c.label}: <span className="font-medium">{c.value}</span>
              </div>
            ))}
          </div>
          {result.ocrData?.notes && (
            <div className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 text-xs text-blue-700">
              {result.ocrData.notes}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function TestInvoiceSuite({ onLoadTestInvoice }) {
  const [results, setResults] = useState([]);
  const [running, setRunning] = useState(false);
  const [open, setOpen] = useState(false);

  const runTests = () => {
    setRunning(true);
    invoiceLog.info("test-suite", "Running test suite", { cases: TEST_CASES.length });
    const testResults = TEST_CASES.map(tc => {
      const ocr = tc.mockOCRData;
      const checks = [];

      checks.push({
        label: `Confiança OCR (mínim ${tc.minConfidence}%)`,
        value: `${ocr.ocr_confidence}%`,
        pass: ocr.ocr_confidence >= tc.minConfidence,
      });

      for (const field of tc.expectedFields) {
        const val = ocr[field];
        const present = Array.isArray(val) ? val.length > 0 : (val !== null && val !== undefined && val !== "");
        checks.push({ label: `Camp '${field}'`, value: present ? "present" : "absent", pass: present });
      }

      if (ocr.subtotal && ocr.vat_amount && ocr.total) {
        const computed = (ocr.subtotal || 0) + (ocr.vat_amount || 0);
        const diff = Math.abs(computed - ocr.total);
        checks.push({ label: "Validació IVA", value: diff < 2 ? `OK (±${diff.toFixed(2)}€)` : `KO (±${diff.toFixed(2)}€)`, pass: diff < 2 });
      }

      if (tc.id === "multi_vat") {
        checks.push({ label: "Tipus IVA detectats", value: `${ocr.tax_lines.length}`, pass: ocr.tax_lines.length >= 3 });
      }

      const allPass = checks.every(c => c.pass);
      const status = allPass ? "pass" : ocr.ocr_confidence >= tc.minConfidence ? "warn" : "fail";
      invoiceLog.info("test-suite", `Test '${tc.id}': ${status}`);
      return { ...tc, status, confidence: ocr.ocr_confidence, checks, ocrData: ocr };
    });

    setResults(testResults);
    setRunning(false);
  };

  const passCount = results.filter(r => r.status === "pass").length;
  const warnCount = results.filter(r => r.status === "warn").length;
  const failCount = results.filter(r => r.status === "fail").length;

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="text-base">🧪</span>
          <span className="text-sm font-semibold text-gray-700">Suite de proves (6 tipus de factura)</span>
          {results.length > 0 && (
            <div className="flex gap-1.5">
              {passCount > 0 && <Badge className="bg-emerald-100 text-emerald-700 border-0 text-xs">{passCount} ✓</Badge>}
              {warnCount > 0 && <Badge className="bg-amber-100 text-amber-700 border-0 text-xs">{warnCount} !</Badge>}
              {failCount > 0 && <Badge className="bg-red-100 text-red-700 border-0 text-xs">{failCount} ✗</Badge>}
            </div>
          )}
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>

      {open && (
        <div className="p-4 space-y-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-xs text-gray-500">Valida el pipeline amb dades OCR simulades sense necessitar imatges reals</p>
            <Button
              size="sm"
              className="bg-[#7B2D3F] hover:bg-[#6a2636] text-white gap-2 h-8"
              onClick={runTests}
              disabled={running}
            >
              {running ? (
                <><div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Executant…</>
              ) : (
                <><Play className="w-3 h-3" /> Executar proves</>
              )}
            </Button>
          </div>

          {results.length === 0 && !running && (
            <p className="text-center py-6 text-gray-400 text-sm">Fes clic a "Executar proves" per validar el pipeline</p>
          )}

          {results.length > 0 && (
            <div className="space-y-2">
              {results.map(r => <TestResult key={r.id} result={r} />)}
            </div>
          )}

          {results.length > 0 && onLoadTestInvoice && (
            <div className="border-t border-gray-100 pt-3">
              <p className="text-xs font-bold text-gray-500 uppercase mb-2">Carregar factura de prova al flux real</p>
              <div className="flex flex-wrap gap-2">
                {TEST_CASES.map(tc => (
                  <button
                    key={tc.id}
                    onClick={() => onLoadTestInvoice(tc)}
                    className="px-2.5 py-1.5 text-xs font-medium border border-gray-200 rounded-lg hover:border-[#7B2D3F] hover:text-[#7B2D3F] transition-colors bg-white"
                  >
                    {tc.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}